import { useRef } from "react";
import { caseRing } from "@/mocks/home";

type Props = {
  onSelectCase: (id: string, originRect: DOMRect, imageSrc: string) => void;
};

/**
 * Grid layout: 3x3, center cell holds the "Real Results" copy safe zone,
 * surrounding 8 cells hold the 8 case images. Every card is a square of
 * equal size. Slot 0 (top-left) is the handoff target for the shared
 * patient image coming from the Concern → Results transition.
 */
export default function SectionResults({ onSelectCase }: Props) {
  return (
    <section id="results" className="relative bg-white overflow-hidden py-space-ds-section-compact md:py-space-ds-section-compact-md">
      <div className="w-full px-6 md:px-10">
        <div className="max-w-ds-wide mx-auto">
        {/* Header — aligned to global wide keyline */}
        <div className="max-w-[640px] mb-12 md:mb-16">
          <div className="text-ds-eyebrow text-ink-muted mb-3">
            03 · Real Results
          </div>
          <h2 className="text-ds-section font-medium text-ink">
            Real change, without losing what makes you look like you.
          </h2>
          <p className="mt-4 text-ds-body-sm text-ink-muted max-w-[480px]">
            A private practice built around individual outcomes — not templates.
            Every result begins with a real conversation.
          </p>
        </div>

        {/* 3x3 grid — 8 cards + center text */}
        <div
          id="ring-canvas"
          className="grid grid-cols-3 gap-4 md:gap-6 mx-auto max-w-ds-case-grid"
        >
          {Array.from({ length: 9 }).map((_, gridIdx) => {
            // Center cell (index 4) is the safe zone
            if (gridIdx === 4) {
              return (
                <div
                  key="center"
                  className="aspect-square flex flex-col items-center justify-center text-center px-4"
                >
                  <div className="text-ds-eyebrow tracking-[0.32em] text-brand mb-3">
                    Real Results
                  </div>
                  <div className="text-ds-meta text-ink-muted mb-5">
                    Careful, nuanced improvements — for natural results.
                  </div>
                  <a
                    href="#treatments"
                    className="inline-flex items-center gap-1.5 text-ds-caption text-ink cursor-pointer border-b border-ink pb-0.5 whitespace-nowrap"
                  >
                    View All Cases <i className="ri-arrow-right-line" />
                  </a>
                </div>
              );
            }

            // Map grid index → case index (skip center)
            const caseIdx = gridIdx < 4 ? gridIdx : gridIdx - 1;
            const c = caseRing[caseIdx];
            const src = c.image;

            return (
              <CaseTile
                key={c.id}
                src={src}
                name={c.name}
                treatment={c.treatment}
                caseIdx={caseIdx}
                onClick={(rect) => onSelectCase(c.id, rect, src)}
              />
            );
          })}
        </div>
        </div>
      </div>
    </section>
  );
}

function CaseTile({
  src,
  name,
  treatment,
  caseIdx,
  onClick,
}: {
  src: string;
  name: string;
  treatment: string;
  caseIdx: number;
  onClick: (rect: DOMRect) => void;
}) {
  const ref = useRef<HTMLButtonElement>(null);

  const handleMove = (e: React.MouseEvent<HTMLButtonElement>) => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const px = (e.clientX - rect.left) / rect.width - 0.5; // -0.5..0.5
    const py = (e.clientY - rect.top) / rect.height - 0.5;
    // Tilt away from cursor (max ~8deg)
    const ry = px * 10;
    const rx = -py * 10;
    el.style.transform = `perspective(700px) rotateX(${rx}deg) rotateY(${ry}deg) translateZ(6px) scale(1.03)`;
  };

  const handleLeave = () => {
    const el = ref.current;
    if (!el) return;
    el.style.transform = "perspective(700px) rotateX(0) rotateY(0) translateZ(0) scale(1)";
  };

  return (
    <button
      ref={ref}
      data-ring-index={caseIdx}
      onClick={() => {
        const el = ref.current;
        if (!el) return;
        // Neutralize the hover tilt/scale so the captured origin rect matches
        // the resting tile — otherwise the fly starts 3% larger and "pops".
        const prevTransition = el.style.transition;
        el.style.transition = "none";
        el.style.transform =
          "perspective(700px) rotateX(0) rotateY(0) translateZ(0) scale(1)";
        const imgEl = el.querySelector("img");
        const rect = (imgEl ?? el).getBoundingClientRect();
        el.style.transition = prevTransition;
        onClick(rect);
      }}
      onMouseMove={handleMove}
      onMouseLeave={handleLeave}
      className="ring-card group relative aspect-square overflow-hidden cursor-pointer p-0 bg-transparent border-0"
      style={{
        borderRadius: 10,
        transition: "transform 220ms cubic-bezier(0.2, 0.8, 0.2, 1)",
        transformStyle: "preserve-3d",
        willChange: "transform",
      }}
    >
      <img
        src={src}
        alt={`${name} — ${treatment}`}
        className="w-full h-full object-cover object-top block"
      />
      <div className="pointer-events-none absolute bottom-0 inset-x-0 p-3 bg-gradient-to-t from-black/55 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-200">
        <div className="text-[11px] text-white/95 leading-tight">{name}</div>
        <div className="text-ds-micro tracking-normal text-white/75">{treatment}</div>
      </div>
    </button>
  );
}